import {
    ArrowPathIcon,
    BanknotesIcon,
    BuildingOfficeIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    MagnifyingGlassIcon,
    UserIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import React, { useEffect, useState } from 'react';
import api from '../../services/api';

const PendingSmsManagement = () => {
  const [smsList, setSmsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSms, setSelectedSms] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [processModalOpen, setProcessModalOpen] = useState(false);
  const [statistics, setStatistics] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // 미처리 SMS 목록 조회
  const fetchPendingSms = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '20',
        search: searchQuery,
        bank: selectedBank,
        status: selectedStatus
      });

      const response = await api.get(`/pending-sms?${params}`);
      if (response.data.success) {
        setSmsList(response.data.data.sms_records || []);
        setTotalPages(response.data.data.total_pages || 1);
        setStatistics(response.data.data.statistics || {});
      }
    } catch (error) {
      console.error('미처리 SMS 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchPendingSms();
  }, [currentPage, searchQuery, selectedBank, selectedStatus]);

  // SMS 상세 조회
  const fetchSmsDetail = async (smsId) => {
    try {
      const response = await api.get(`/pending-sms/${smsId}`);
      if (response.data.success) {
        setSelectedSms(response.data.data);
        setDetailModalOpen(true);
      }
    } catch (error) {
      console.error('SMS 상세 조회 실패:', error);
    }
  };

  // SMS 수동 매칭
  const matchSms = async (smsId, tenantId, billingStatementId) => {
    try {
      const response = await api.post(`/pending-sms/${smsId}/match`, {
        tenant_id: tenantId,
        billing_statement_id: billingStatementId
      });
      
      if (response.data.success) {
        window.alert('SMS 매칭이 완료되었습니다.');
        fetchPendingSms();
        setDetailModalOpen(false);
      }
    } catch (error) {
      console.error('SMS 매칭 실패:', error);
      window.alert('SMS 매칭에 실패했습니다.');
    }
  };

  // SMS 입금처리
  const processSms = async (smsId) => {
    try {
      const response = await api.post(`/pending-sms/${smsId}/process`);
      
      if (response.data.success) {
        window.alert('SMS 입금처리가 완료되었습니다.');
        fetchPendingSms();
        setProcessModalOpen(false);
      }
    } catch (error) {
      console.error('SMS 입금처리 실패:', error);
      window.alert('SMS 입금처리에 실패했습니다.');
    }
  };

  // 상태별 색상 반환
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'unmatched':
        return 'bg-red-100 text-red-800';
      case 'matched':
        return 'bg-blue-100 text-blue-800';
      case 'processed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // 상태별 한글명 반환
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return '대기중';
      case 'unmatched':
        return '매칭실패';
      case 'matched':
        return '매칭완료';
      case 'processed':
        return '처리완료';
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    미처리 SMS
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {statistics.pending || 0}건
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XMarkIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    매칭실패
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {statistics.unmatched || 0}건
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    매칭완료
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {statistics.matched || 0}건
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BanknotesIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    처리완료
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {statistics.processed || 0}건
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              검색
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="송금자명, 전화번호 검색"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              은행
            </label>
            <select
              value={selectedBank}
              onChange={(e) => setSelectedBank(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">전체</option>
              <option value="국민은행">국민은행</option>
              <option value="신한은행">신한은행</option>
              <option value="우리은행">우리은행</option>
              <option value="하나은행">하나은행</option>
              <option value="농협은행">농협은행</option>
              <option value="기업은행">기업은행</option>
              <option value="카카오뱅크">카카오뱅크</option>
              <option value="토스뱅크">토스뱅크</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              상태
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">전체</option>
              <option value="pending">대기중</option>
              <option value="unmatched">매칭실패</option>
              <option value="matched">매칭완료</option>
              <option value="processed">처리완료</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchPendingSms}
              className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              새로고침
            </button>
          </div>
        </div>
      </div>

      {/* SMS 목록 테이블 */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            미처리 SMS 목록
          </h3>
          
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      송금자
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      금액
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      은행
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      상태
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      수신시간
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      작업
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {smsList.map((sms) => (
                    <tr key={sms.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <UserIcon className="h-4 w-4 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {sms.sender_name || '미확인'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {sms.sender_phone || '미확인'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {sms.amount?.toLocaleString()}원
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">
                            {sms.bank_name || '미확인'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(sms.status)}`}>
                          {getStatusText(sms.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                          {sms.received_at ? format(new Date(sms.received_at), 'MM/dd HH:mm', { locale: ko }) : '미확인'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => fetchSmsDetail(sms.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="상세보기"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {sms.status === 'matched' && (
                            <button
                              onClick={() => {
                                setSelectedSms(sms);
                                setProcessModalOpen(true);
                              }}
                              className="text-green-600 hover:text-green-900"
                              title="입금처리"
                            >
                              <CheckCircleIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  이전
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  다음
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">{currentPage}</span> / <span className="font-medium">{totalPages}</span> 페이지
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    >
                      이전
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    >
                      다음
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SMS 상세 모달 */}
      {detailModalOpen && selectedSms && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">SMS 상세 정보</h3>
                <button
                  onClick={() => setDetailModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">송금자</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSms.sender_name || '미확인'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">전화번호</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSms.sender_phone || '미확인'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">금액</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSms.amount?.toLocaleString()}원</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">은행</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSms.bank_name || '미확인'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">SMS 내용</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {selectedSms.message_content}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">수신시간</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedSms.received_at ? format(new Date(selectedSms.received_at), 'yyyy-MM-dd HH:mm:ss', { locale: ko }) : '미확인'}
                  </p>
                </div>

                {selectedSms.status === 'unmatched' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <p className="text-sm text-yellow-800">
                      이 SMS는 자동 매칭에 실패했습니다. 수동으로 임차인과 청구서를 매칭해주세요.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setDetailModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  닫기
                </button>
                {selectedSms.status === 'unmatched' && (
                  <button
                    onClick={() => {
                      // 수동 매칭 로직 구현 필요
                      window.alert('수동 매칭 기능은 추후 구현 예정입니다.');
                    }}
                    className="px-4 py-2 border border-transparent rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    수동 매칭
                  </button>
                )}
                {selectedSms.status === 'matched' && (
                  <button
                    onClick={() => {
                      setProcessModalOpen(true);
                      setDetailModalOpen(false);
                    }}
                    className="px-4 py-2 border border-transparent rounded-md text-white bg-green-600 hover:bg-green-700"
                  >
                    입금처리
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 입금처리 확인 모달 */}
      {processModalOpen && selectedSms && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">입금처리 확인</h3>
                <button
                  onClick={() => setProcessModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  다음 SMS를 입금처리하시겠습니까?
                </p>
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-sm">
                    <strong>송금자:</strong> {selectedSms.sender_name}<br/>
                    <strong>금액:</strong> {selectedSms.amount?.toLocaleString()}원<br/>
                    <strong>은행:</strong> {selectedSms.bank_name}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setProcessModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={() => processSms(selectedSms.id)}
                  className="px-4 py-2 border border-transparent rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  입금처리
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingSmsManagement;